import axiosInstance from '../axiosInstance';

export const getAccountChangeNotices = async () => {
  const response = await axiosInstance.get('/users/me/account-change-notices');
  return response.data.data;
};

export const dismissAccountChangeNotice = (id) =>
  axiosInstance.patch(`/users/me/account-change-notices/${id}/dismiss`);

export const getEmailNotificationSetting = async () => {
  const response = await axiosInstance.get('/users/me/email-notifications');
  return response.data.data;
};

export const updateEmailNotificationSetting = async (emailEnabled) => {
  const response = await axiosInstance.put('/users/me/email-notifications', { emailEnabled });
  return response.data.data;
};

export const registerUser = async (data) => {
  try {
    const response = await axiosInstance.post('/users/register', {
      ...data,
      triggerSource:
        sessionStorage.getItem('signup_trigger_source') ?? 'direct',
    });
    sessionStorage.removeItem('signup_trigger_source');
    return response.data;
  } catch (error) {
    console.error('Register API Error:', error);
    throw error;
  }
};

export const validateEmail = async (email) => {
  try {
    const response = await axiosInstance.get(
      `/users/validate-email?email=${email}`,
    );
    return response.data;
  } catch (error) {
    console.error('ValidateEmail API Error:', error);
    throw error;
  }
};

export const registerVerifyEmail = async (token) => {
  try {
    const response = await axiosInstance.get(
      `/users/verify?token=${encodeURIComponent(token)}`,
    );
    return response.data;
  } catch (error) {
    console.error('VerifyEmail API Error:', error);
    throw error;
  }
};

export const resendVerifyEmail = async (email) => {
  try {
    const response = await axiosInstance.post(
      `/users/verify/resend?email=${email}`,
    );
    return response.data;
  } catch (error) {
    console.error('ResendVerifyEmail API Error:', error);
    throw error;
  }
};

export const cancelVerifyEmail = async (email) => {
  try {
    const response = await axiosInstance.post(
      `/users/verify/cancel?email=${email}`,
    );
    return response.data;
  } catch (error) {
    console.error('CancelVerifyEmail API Error:', error);
    throw error;
  }
};

export const requestPasswordReset = async (email) => {
  try {
    const response = await axiosInstance.post(
      `/users/password/reset-request?email=${email}`,
    );
    return response.data;
  } catch (error) {
    console.error('requestPasswordReset API Error:', error);
    throw error;
  }
};

export const resendPasswordResetEmail = async (email) => {
  try {
    const response = await axiosInstance.post(
      `/users/password/reset-resend?email=${email}`,
    );
    return response.data;
  } catch (error) {
    console.error('resendPasswordResetEmail API Error:', error);
    throw error;
  }
};

export const verifyEmailPasswordReset = async (token) => {
  try {
    const response = await axiosInstance.get(
      `/users/password/reset-verify?token=${token}`,
    );
    return response.data;
  } catch (error) {
    console.error('requestPasswordReset API Error:', error);
    throw error;
  }
};

export const passwordReset = async ({ token, newPassword }) => {
  try {
    const response = await axiosInstance.post('/users/password/reset', {
      token,
      newPassword,
    });
    return response.data;
  } catch (error) {
    console.error('passwordReset API Error:', error);
    throw error;
  }
};

export const loginUser = async (data) => {
  try {
    const response = await axiosInstance.post('/users/login', data, {
      withCredentials: true,
    });
    return response.data;
  } catch (error) {
    console.error('Login API Error:', error);
    throw error;
  }
};

export const googleAuth = async (code, state, redirectUri) => {
  try {
    const params = new URLSearchParams({ code });
    if (state) params.append('state', state);
    if (redirectUri) params.append('redirectUri', redirectUri);

    const response = await axiosInstance.post(
      `/users/social/google/auth?${params.toString()}`,
    );
    return response.data;
  } catch (error) {
    console.error('GoogleAuth API Error:', error);
    throw error;
  }
};

export const socialValidateEmail = async (email) => {
  try {
    const response = await axiosInstance.get(
      `/users/social/google/validate-email?email=${email}`,
    );
    return response.data;
  } catch (error) {
    console.error('SocialValidateEmail API Error:', error);
    throw error;
  }
};

export const googleRegister = async (data) => {
  try {
    const response = await axiosInstance.post(
      `/users/social/google/register`,
      {
        ...data,
        triggerSource:
          sessionStorage.getItem('signup_trigger_source') ?? 'direct',
      },
      { withCredentials: true, skipLoading: true },
    );
    sessionStorage.removeItem('signup_trigger_source');
    return response.data;
  } catch (error) {
    console.error('GoogleRegister API Error:', error);
    throw error;
  }
};

export const googleLogin = async (email) => {
  try {
    const response = await axiosInstance.post(
      `/users/social/google/login?socialEmail=${encodeURIComponent(email)}`,
      {},
      { withCredentials: true, skipLoading: true },
    );
    return response.data;
  } catch (error) {
    console.error('GoogleLogin API Error:', error);
    throw error;
  }
};

/**
 * Apple 웹 로그인 시작 — 서버가 Apple 인증 페이지로 리다이렉트한다.
 * Apple 은 response_mode=form_post 로 POST 콜백을 보내기 때문에
 * 구글처럼 프론트가 직접 콜백을 받을 수 없고, 서버를 거쳐야 한다.
 */
export const startAppleLogin = (returnTo = '/') => {
  const base =
    process.env.REACT_APP_API_URL || 'http://localhost:8080/api/v1';
  const params = new URLSearchParams({ returnTo });
  window.location.href = `${base}/users/social/apple/web/start?${params.toString()}`;
};

/**
 * 마이페이지에서 애플을 연결한다 — 로그인이 아니라 지금 계정에 붙이는 흐름.
 *
 * <p>서버가 state 에 내 userId 를 실어 보내므로, 콜백에서 새 계정을 만들지 않는다.
 * 결과는 returnTo 에 appleConnect=success|failed 로 되돌아온다.
 */
export const startAppleConnect = (returnTo = '/mypage') => {
  const base = process.env.REACT_APP_API_URL || 'http://localhost:8080/api/v1';
  const params = new URLSearchParams({ returnTo });
  window.location.href = `${base}/users/social/apple/web/connect?${params.toString()}`;
};

/** 콜백에서 받은 1회용 티켓을 실제 토큰으로 교환한다. */
export const exchangeAppleTicket = async (ticket) => {
  try {
    const response = await axiosInstance.post(
      `/users/social/apple/web/exchange?ticket=${encodeURIComponent(ticket)}`,
      {},
      { withCredentials: true, skipLoading: true },
    );
    return response.data;
  } catch (error) {
    console.error('AppleLogin API Error:', error);
    throw error;
  }
};

export const saveAdditionalInfo = async ({
  email,
  socialEmail,
  location,
  birth,
  gender,
  profileFile,
  isSocial,
}) => {
  try {
    const formData = new FormData();
    formData.append('email', email);
    formData.append('socialEmail', socialEmail);
    formData.append('location', location);
    formData.append('birth', birth);
    formData.append('gender', gender);
    formData.append('isSocial', isSocial);
    if (profileFile) {
      formData.append('profileUrl', profileFile);
    }

    const response = await axiosInstance.post(
      '/users/additional-info',
      formData,
      {
        headers: { 'Content-Type': 'application/json' },
        withCredentials: true,
      },
    );

    return response.data;
  } catch (error) {
    console.error('SaveUserAdditionalInfo API Error:', error);
    throw error;
  }
};

export const reissueToken = async () => {
  try {
    const response = await axiosInstance.post(
      '/users/reissue',
      {},
      { withCredentials: true },
    );
    return response.data;
  } catch (error) {
    console.error('ReissueToken API Error:', error);
    throw error;
  }
};

export const autoLogin = async () => {
  try {
    const response = await axiosInstance.get('/users/auto-login', {
      withCredentials: true,
    });
    return response.data;
  } catch (error) {
    console.error('AutoLogin API Error:', error);
    throw error;
  }
};

export const getUserProfile = async () => {
  try {
    const response = await axiosInstance.get('/users/me', {
      withCredentials: true,
      skipAuthRefresh: true,
    });
    return response.data;
  } catch (error) {
    console.error('GetUserProfile API Error:', error);
    throw error;
  }
};

export const getUserProfileById = async (userId) => {
  try {
    const response = await axiosInstance.get(`/users/user/${userId}`);
    return response.data.data;
  } catch (error) {
    console.error('GetUserProfileById API Error:', error);
    throw error;
  }
};

export const logoutUser = async () => {
  try {
    const response = await axiosInstance.post(
      '/users/logout',
      {},
      {
        withCredentials: true,
      },
    );
    return response.data;
  } catch (error) {
    console.error('Logout API Error:', error);
    throw error;
  }
};

export const updateUserProfile = async (data) => {
  try {
    const response = await axiosInstance.post('/users/my-profile', data, {
      withCredentials: true,
    });
    return response.data;
  } catch (error) {
    console.error('UpdateUserProfile API Error:', error);
    throw error;
  }
};

export const uploadProfileImage = async (base64) => {
  // 실패 피드백은 컴포넌트가 토스트로 보여주므로 전역 에러 페이지로 보내지 않는다
  const res = await axiosInstance.patch(
    '/users/profile-image',
    { profileImage: base64 },
    { skipGlobalError: true },
  );

  return res.data.profileImageUrl;
};

export const deleteProfileImage = async () => {
  await axiosInstance.delete('/users/profile-image', { skipGlobalError: true });
};

/** provider 는 'GOOGLE' 또는 'APPLE'. */
export const disconnectSocial = async (provider) => {
  try {
    const response = await axiosInstance.delete(
      `/users/social/${provider}/disconnect`,
    );
    return response.data;
  } catch (error) {
    console.error('Disconnect Social API Error:', error);
    throw error;
  }
};

/** @deprecated disconnectSocial('GOOGLE') 을 쓴다. */
export const disconnectGoogle = () => disconnectSocial('GOOGLE');

export const updateEmail = async (newEmail) => {
  try {
    const response = await axiosInstance.patch('/users/email', {
      email: newEmail,
    });
    return response.data;
  } catch (error) {
    console.error('Update Email API Error:', error);
    throw error;
  }
};

export const sendVerificationCode = async (email) => {
  try {
    const response = await axiosInstance.post(
      `/users/verify/code/send?email=${email}`,
    );
    return response.data;
  } catch (error) {
    console.error('Send Verification Code API Error:', error);
    throw error;
  }
};

export const confirmVerificationCode = async (email, code) => {
  try {
    const response = await axiosInstance.post(
      `/users/verify/code/confirm?email=${email}&code=${code}`,
    );
    return response.data;
  } catch (error) {
    console.error('Confirm Verification Code API Error:', error);
    throw error;
  }
};

export const updatePassword = async (newPassword) => {
  try {
    const response = await axiosInstance.patch('/users/password', {
      password: newPassword,
    });
    return response.data;
  } catch (error) {
    console.error('Update Password API Error:', error);
    throw error;
  }
};

export const verifyPassword = async (password) => {
  try {
    const response = await axiosInstance.post('/users/verify-password', {
      password,
    });
    return response.data;
  } catch (error) {
    console.error('Verify Password API Error:', error);
    throw error;
  }
};

export const deleteAccount = async (force = false) => {
  try {
    const response = await axiosInstance.delete('/users/me', {
      params: { force },
      withCredentials: true,
    });
    return response.data;
  } catch (error) {
    console.error('Delete Account API Error:', error);
    throw error;
  }
};

/**
 * Admin - 회원 목록 조회
 * @param {{ page?: number, size?: number, keyword?: string, filter?: 'ALL'|'BUSINESS'|'CREATOR'|'DELETED' }} params
 */
export const getAdminUsers = async ({
  page = 0,
  size = 12,
  keyword = '',
  filter = 'ALL',
} = {}) => {
  const response = await axiosInstance.get('/users/admin', {
    params: { page, size, keyword: keyword || undefined, filter },
  });
  return response.data.data;
};

/** Admin - 크리에이터 부여(true)/해제(false) */
export const setAdminUserCreator = async (userId, enabled) => {
  const response = await axiosInstance.patch(`/users/admin/${userId}/creator`, {
    enabled,
  });
  return response.data.data;
};

/** Admin - 기업 계정 전환(true)/해제(false) */
export const setAdminUserBusiness = async (userId, enabled) => {
  const response = await axiosInstance.patch(`/users/admin/${userId}/business`, {
    enabled,
  });
  return response.data.data;
};

/** Admin - 계정 차단(true, reason)/해제(false) */
export const setAdminUserBlocked = async (userId, { blocked, reason = '' }) => {
  const response = await axiosInstance.patch(`/users/admin/${userId}/block`, {
    blocked,
    reason,
  });
  return response.data.data;
};
